import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import COLORS from '../../constants/colors';

interface FieldGridProps {
  /** Columns at md and up. Below that the grid always collapses to one column. */
  columns?: 1 | 2 | 3 | 4;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export function FieldGrid({ columns = 2, children, sx }: FieldGridProps) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `repeat(${columns}, minmax(0, 1fr))` },
        columnGap: '24px',
        rowGap: '16px',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

interface FieldProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Let the field span the full grid width — for long values and JSON. */
  fullWidth?: boolean;
  children?: React.ReactNode;
}

/**
 * A label/value pair. Labels were previously `Typography variant="h6"`, which is
 * a heading style — it made every field shout and buried the actual values.
 */
export function Field({ label, value, fullWidth, children }: FieldProps) {
  const content = children ?? value;
  const isEmpty = content === null || content === undefined || content === '';

  return (
    <Box sx={{ minWidth: 0, gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <Typography
        component="dt"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: COLORS.LIGHT_GRAY,
          mb: '4px',
        }}
      >
        {label}
      </Typography>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: '0.875rem',
          lineHeight: '20px',
          color: isEmpty ? COLORS.LIGHT_GRAY : COLORS.MAIN_DARK,
          overflowWrap: 'anywhere',
        }}
      >
        {isEmpty ? '—' : content}
      </Box>
    </Box>
  );
}
